import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Shield, Eye, Heart, Zap, Globe, Building2,
  Users, Award, CheckCircle, ArrowRight, Quote
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UrgentCarePointerBlock from '../components/UrgentCarePointerBlock';
import { COMPANY_DETAILS, COMPANY_STATEMENT } from '../data/company';

const VALUES = [
  {
    icon: Shield,
    title: 'Trust First',
    desc: 'Every clinic on VietClinics passes a structured quality review. We list only clinics we would recommend to our own families.',
    color: '#1FA7A1',
  },
  {
    icon: Eye,
    title: 'Transparency',
    desc: 'Prices are shown upfront. No hidden fees, no surprises at checkout. What you see is exactly what you pay.',
    color: '#1E5BA8',
  },
  {
    icon: Globe,
    title: 'Accessibility',
    desc: 'Available in multiple languages across Vietnam, we work to make quality healthcare easier to reach for international patients.',
    color: '#2ECC71',
  },
  {
    icon: Zap,
    title: 'Innovation',
    desc: 'From AI-powered clinic matching to real-time booking, we are constantly pushing the boundaries of healthcare technology.',
    color: '#F59E0B',
  },
  {
    icon: Heart,
    title: 'Patient-First',
    desc: 'Every product decision starts with one question: does this make the patient\'s experience better, safer, and easier?',
    color: '#EF4444',
  },
];


const AboutUsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About VietClinics — Vietnam's Healthcare Concierge Platform</title>
        <meta name="description" content="Learn about VietClinics, an independent healthcare coordination service helping international patients explore provider options and arrange care in Vietnam." />
        <link rel="canonical" href="https://vietclinics.com/about" />
      </Helmet>
      <Header />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d2b55 0%, #1E5BA8 55%, #1FA7A1 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(31,167,161,0.25) 0%, transparent 60%)',
        }} />
        <div className="container mx-auto px-4 max-w-5xl py-24 md:py-32 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-[11px] font-bold px-4 py-2 rounded-full mb-6 tracking-widest uppercase">
            <span className="w-1.5 h-1.5 bg-[#2EE8C4] rounded-full" />
            Our Story
          </div>
          <h1
            className="font-extrabold text-white leading-[1.05] mb-6 tracking-tight"
            style={{ fontSize: 'clamp(2.4rem, 5vw, 3.6rem)' }}
          >
            Building Vietnam's Most Trusted<br />
            <span style={{ color: '#5DFFD4' }}>Healthcare Platform</span>
          </h1>
          <p className="text-white/75 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            VietClinics was built because finding a trusted clinic in Vietnam shouldn't be hard.
            We exist to make healthcare discovery simple, transparent, and accessible for everyone.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 30 C480 0 960 48 1440 20 L1440 48 L0 48 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section className="py-20" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">What Drives Us</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Mission & Vision</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="bg-white rounded-[28px] p-8 md:p-10 border border-slate-100"
              style={{ boxShadow: '0 8px 32px rgba(31,167,161,0.08)' }}
            >
              <div className="w-14 h-14 bg-[#1FA7A1]/10 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="h-7 w-7 text-[#1FA7A1]" />
              </div>
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">Our Mission</p>
              <h3 className="text-2xl font-extrabold text-slate-900 mb-4 leading-tight">
                Make healthcare discovery simple, transparent, and accessible.
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We believe every patient — whether Vietnamese or an international visitor — deserves to find a trusted clinic
                without friction. Our mission is to eliminate uncertainty and replace it with clarity, confidence, and convenience.
              </p>
            </div>
            <div
              className="bg-white rounded-[28px] p-8 md:p-10 border border-slate-100"
              style={{ boxShadow: '0 8px 32px rgba(30,91,168,0.08)' }}
            >
              <div className="w-14 h-14 bg-[#1E5BA8]/10 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-[#1E5BA8]" />
              </div>
              <p className="text-[11px] font-bold text-[#1E5BA8] uppercase tracking-widest mb-3">Our Vision</p>
              <h3 className="text-2xl font-extrabold text-slate-900 mb-4 leading-tight">
                Become Vietnam's leading trusted healthcare platform by 2027.
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We envision a future where every patient in Vietnam — regardless of language, location, or background — can discover,
                compare, and book the right clinic in under 60 seconds. Technology, trust, and care at scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY WE EXIST ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">The Problem We Solve</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-6">
                Why VietClinics Exists
              </h2>
              <div className="space-y-5 text-slate-600 leading-relaxed">
                <p>
                  Finding quality healthcare in Vietnam used to mean relying on word-of-mouth recommendations, outdated Google searches,
                  and hoping the clinic you found was actually reputable.
                </p>
                <p>
                  International visitors faced an even bigger challenge — language barriers, unclear pricing, and no way to verify
                  credentials before booking.
                </p>
                <p>
                  We built VietClinics to solve this. Every clinic on our platform is manually verified, every price is shown upfront,
                  and every booking is tracked so patients always know what's happening.
                </p>
              </div>
              <div className="mt-8 flex flex-col gap-3">
                {[
                  'Verified credentials for every listed clinic',
                  'Transparent pricing — no hidden costs ever',
                  'Multilingual support for international patients',
                  'Real reviews from verified patients only',
                ].map((point) => (
                  <div key={point} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#2ECC71] flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div
                className="rounded-[28px] overflow-hidden"
                style={{ boxShadow: '0 24px 64px rgba(31,167,161,0.12)' }}
              >
                <img
                  src="https://images.pexels.com/photos/5619462/pexels-photo-5619462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern clinic in Vietnam"
                  className="w-full h-80 object-cover"
                />
              </div>
              <div
                className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 border border-slate-100"
                style={{ boxShadow: '0 12px 40px rgba(0,0,0,0.10)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2ECC71]/15 rounded-xl flex items-center justify-center">
                    <Shield className="h-5 w-5 text-[#2ECC71]" />
                  </div>
                  <div>
                    <p className="font-extrabold text-slate-900 text-sm">27-Point Verified</p>
                    <p className="text-[11px] text-slate-400">Clinic quality check</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ── */}
      <section className="py-20" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">What We Stand For</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <div
                key={v.title}
                className={`bg-white rounded-[24px] p-6 border border-slate-100 transition-all duration-300 hover:-translate-y-1.5 ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                  style={{ background: `${v.color}15` }}
                >
                  <v.icon className="h-6 w-6" style={{ color: v.color }} />
                </div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">{v.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST & SAFETY ── */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #0d2b55 0%, #1E5BA8 60%, #1FA7A1 100%)' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest mb-3">Built on Integrity</p>
              <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-6 tracking-tight">
                Trust & Safety Is<br />At Our Core
              </h2>
              <p className="text-white/75 text-base leading-relaxed mb-8">
                We don't just list clinics. We verify them. Our team personally reviews every clinic application
                before they appear on VietClinics. This includes license checks, facility inspections, and
                patient feedback monitoring.
              </p>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { icon: Shield, text: 'Medical license verified by our team' },
                  { icon: Award, text: 'Facility quality standards enforced' },
                  { icon: Users, text: 'Patient reviews monitored and moderated' },
                  { icon: CheckCircle, text: 'Real-time booking confirmations' },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/15">
                    <Icon className="h-5 w-5 text-[#2EE8C4] flex-shrink-0" />
                    <span className="text-white/90 font-medium text-sm">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { val: '27', label: 'Quality checkpoints per clinic', color: '#2EE8C4' },
                { val: '<2h', label: 'Average booking confirmation', color: '#F59E0B' },
                { val: '100%', label: 'License verification rate', color: '#2ECC71' },
                { val: '24/7', label: 'Patient support available', color: '#60A5FA' },
              ].map(({ val, label, color }) => (
                <div
                  key={label}
                  className="bg-white/10 backdrop-blur-sm rounded-[20px] p-6 border border-white/15 text-center"
                >
                  <div className="text-3xl font-extrabold mb-2" style={{ color }}>{val}</div>
                  <div className="text-white/70 text-xs font-medium leading-snug">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20" style={{ background: '#F8FAFC' }}>
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div
            className="bg-white rounded-[32px] p-10 md:p-14 border border-slate-100"
            style={{ boxShadow: '0 16px 56px rgba(31,167,161,0.10)' }}
          >
            <div className="w-16 h-16 bg-[#1FA7A1]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Heart className="h-8 w-8 text-[#1FA7A1]" />
            </div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-4">
              Ready to Experience VietClinics?
            </h2>
            <p className="text-slate-500 text-base leading-relaxed mb-8 max-w-lg mx-auto">
              Request treatment through VietClinics and let our team help you explore provider options in Vietnam.
              It takes less than 60 seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/treatments"
                className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#188B86] text-white font-bold px-8 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5"
                style={{ boxShadow: '0 8px 24px rgba(31,167,161,0.30)' }}
                data-testid="about-cta-explore-treatments"
              >
                Explore Treatments
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/clinic-registration"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1E5BA8] text-[#1E5BA8] font-bold px-8 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1E5BA8]/5"
              >
                Register Your Clinic
              </Link>
            </div>
          </div>
        </div>
      </section>

      <UrgentCarePointerBlock testId="about-urgent-pointer" />

      {/* ── Company details ── */}
      <section className="py-14 bg-white" data-testid="about-company-details">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="rounded-3xl border border-slate-100 p-8 md:p-10" style={{ background: '#F8FAFC' }}>
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2 flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5" /> Company details
            </p>
            <p className="text-slate-900 font-semibold leading-relaxed">{COMPANY_STATEMENT}</p>
            <dl className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
              <div><dt className="text-slate-500">Company Registration No.</dt><dd className="font-semibold text-slate-900">{COMPANY_DETAILS.registrationNo}</dd></div>
              <div><dt className="text-slate-500">Registered office</dt><dd className="font-semibold text-slate-900">{COMPANY_DETAILS.registeredOffice}</dd></div>
              <div><dt className="text-slate-500">Vietnam operations</dt><dd className="font-semibold text-slate-900">{COMPANY_DETAILS.vietnamOperations}</dd></div>
            </dl>
            <Link to="/company-details" className="inline-flex items-center gap-1.5 mt-6 text-sm font-bold text-[#1FA7A1] hover:gap-2.5 transition-all" data-testid="about-company-details-link">
              View full company details <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUsPage;
